import { Box, Button, Input, Label, Select, Text, VStack } from '@betterment/samba-react';
import { MultiSelect, createMultiSelectCollection } from '@betterment/shared-components';
import { safeDateString } from '@betterment/shared-components/src/components/activity/utils/dateUtils';

import { CsvDownloadButton } from './CsvDownloadButton';

import { trackClick } from '../../features/track';
import { ActivityItemTypeFilterOption } from '../../graphql/schema';
import {
  AccountFilterOption,
  ActivityFilter,
  FilterOptions,
  TransactionTypeFilterOption,
} from '../hooks/useFilterOptions';

type FilterRowProps = {
  filterOptions: FilterOptions;
  currentFilter: ActivityFilter;
  setFilters: (filters: ActivityFilter) => void;
  resetFilters: () => void;
  onDownloadCsv?: () => void;
  errors?: {
    customTimeframe?: string;
  };
};

export const FilterRow: React.FC<FilterRowProps> = ({
  filterOptions,
  currentFilter,
  setFilters,
  resetFilters,
  onDownloadCsv,
  errors,
}) => {
  const accountsCollection = createMultiSelectCollection<AccountFilterOption>({
    items: filterOptions.accounts,
    groupBy: item => (item.isClosed ? 'Closed accounts' : ''),
  });

  const sortedAccountGroups = accountsCollection.group().sort(([groupA], [groupB]) => {
    if (groupA === 'Closed accounts' && groupB !== 'Closed accounts') return 1;
    if (groupA !== 'Closed accounts' && groupB === 'Closed accounts') return -1;
    return 0;
  });

  const transactionTypesCollection = createMultiSelectCollection<TransactionTypeFilterOption>({
    items: filterOptions.transactionType,
  });

  const isCustomTimeframe = currentFilter.timeframe.isCustom;

  const handleDateChange = (type: 'start' | 'end', value: string) => {
    const parsedDate = new Date(value);
    const customStart = currentFilter.timeframe.dateRange.startDateInclusive;
    const customEnd = currentFilter.timeframe.dateRange.endDateInclusive;

    const updated = {
      label: 'Custom',
      dateRange: {
        startDateInclusive: type === 'start' ? parsedDate : customStart,
        endDateInclusive: type === 'end' ? parsedDate : customEnd,
      },
      isCustom: true,
    };

    setFilters({ ...currentFilter, timeframe: updated });
  };

  const findDateRangeValue = (label: string) => {
    const dateRange = filterOptions.timeframe.find(t => t.label === label);

    if (dateRange === undefined) {
      throw new Error(`Date range not found for label: ${label}`);
    }
    return dateRange.value;
  };

  const updateDateRange = (label: string) => {
    trackClick({ name: `Activity ${label}` });
    if (label === 'Custom') {
      setFilters({
        ...currentFilter,
        timeframe: {
          label,
          dateRange: {
            startDateInclusive: null,
            endDateInclusive: null,
          },
          isCustom: true,
        },
      });
    } else {
      const dateRange = findDateRangeValue(label);
      setFilters({
        ...currentFilter,
        timeframe: {
          label,
          dateRange,
          isCustom: false,
        },
      });
    }
  };

  const isFilterApplied =
    currentFilter.accountId.length > 0 ||
    currentFilter.transactionType.length > 0 ||
    currentFilter.timeframe.isCustom ||
    currentFilter.timeframe.label !== filterOptions.timeframe[0].label;

  return (
    <VStack css={{ gap: 'space.200' }}>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: 'space.400',
        }}
      >
        <VStack css={{ flex: 1, gap: 'space.100' }}>
          <Select onValueChange={updateDateRange} value={currentFilter.timeframe.label}>
            <Select.Trigger aria-label="Date range filter">
              <Select.Value />
            </Select.Trigger>
            <Select.Content sideOffset={4}>
              {filterOptions.timeframe.map(val => (
                <Select.Item value={val.label} key={val.label}>
                  {val.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
          {isCustomTimeframe && (
            <Box css={{ display: 'flex', gap: 'space.100', flexDirection: 'column' }}>
              <Box css={{ display: 'flex', gap: 'space.100' }}>
                <Box css={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="fromDate">From</Label>
                  <Input
                    id="fromDate"
                    type="date"
                    value={safeDateString(currentFilter.timeframe.dateRange.startDateInclusive) ?? undefined}
                    max={safeDateString(new Date()) ?? undefined}
                    onChange={e => handleDateChange('start', e.currentTarget.value)}
                  />
                </Box>
                <Box css={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="toDate">To</Label>
                  <Input
                    id="toDate"
                    type="date"
                    value={safeDateString(currentFilter.timeframe.dateRange.endDateInclusive) ?? undefined}
                    max={safeDateString(new Date()) ?? undefined}
                    onChange={e => handleDateChange('end', e.currentTarget.value)}
                  />
                </Box>
              </Box>
              {errors?.customTimeframe && (
                <Text variant="text50" color="text.critical" role="alert">
                  {errors.customTimeframe}
                </Text>
              )}
            </Box>
          )}
        </VStack>

        <Box css={{ flex: 1, minWidth: '0px' }}>
          <MultiSelect
            collection={accountsCollection}
            value={currentFilter.accountId}
            onValueChange={v => {
              trackClick({ name: 'ActivityAccountFilter' });
              setFilters({ ...currentFilter, accountId: v.value });
            }}
          >
            <MultiSelect.Trigger aria-label="Accounts filter">
              <MultiSelect.MultiValueText placeholder="Filter accounts...">
                {({ selectedItems, valueAsString }) => {
                  switch (selectedItems.length) {
                    case 0:
                      return null;
                    case 1:
                      return valueAsString;
                    case accountsCollection.items.length:
                      return 'All accounts';
                    default:
                      return `${selectedItems.length} accounts`;
                  }
                }}
              </MultiSelect.MultiValueText>
            </MultiSelect.Trigger>
            <MultiSelect.Content>
              {sortedAccountGroups.map(([type, group]) => (
                <MultiSelect.ItemGroup key={type}>
                  {type && (
                    <>
                      <MultiSelect.Separator css={{ marginTop: 'space.100' }} />
                      <MultiSelect.ItemGroupLabel>{type}</MultiSelect.ItemGroupLabel>
                    </>
                  )}
                  {group.map(item => (
                    <MultiSelect.Item key={item.value} item={item}>
                      {item.label}
                    </MultiSelect.Item>
                  ))}
                </MultiSelect.ItemGroup>
              ))}
            </MultiSelect.Content>
          </MultiSelect>
        </Box>
        <Box css={{ flex: 1, minWidth: '0px' }}>
          <MultiSelect
            collection={transactionTypesCollection}
            value={currentFilter.transactionType}
            onValueChange={v => {
              trackClick({ name: 'ActivityTypeFilter' });
              setFilters({
                ...currentFilter,
                transactionType: v.value as ActivityItemTypeFilterOption[],
              });
            }}
          >
            <MultiSelect.Trigger aria-label="Transaction types filter">
              <MultiSelect.MultiValueText placeholder="Filter transaction types...">
                {({ selectedItems, valueAsString }) => {
                  switch (selectedItems.length) {
                    case 0:
                      return null;
                    case 1:
                      return valueAsString;
                    case transactionTypesCollection.items.length:
                      return 'All transaction types';
                    default:
                      return `${selectedItems.length} transaction types`;
                  }
                }}
              </MultiSelect.MultiValueText>
            </MultiSelect.Trigger>
            <MultiSelect.Content>
              {transactionTypesCollection.items.map(item => (
                <MultiSelect.Item key={item.value} item={item}>
                  {item.label}
                </MultiSelect.Item>
              ))}
            </MultiSelect.Content>
          </MultiSelect>
        </Box>
        {onDownloadCsv && <CsvDownloadButton disabled={false} onClick={onDownloadCsv} />}
      </Box>
      {isFilterApplied && (
        <Button variant="textSecondary" onClick={resetFilters}>
          Reset
        </Button>
      )}
    </VStack>
  );
};
